import axios from 'axios';
import { baseApiUrl } from '../../utils';
import { tenant } from '../../config/tenant';
import { TLogin } from './login.types';

const baseUrl = process.env.NODE_ENV === 'development' ? baseApiUrl.dev : baseApiUrl.prod;

interface LoginCredentials {
	email: string;
	password: string;
}

export const loginService = async ({ email, password }: LoginCredentials): Promise<TLogin> => {
	const url = `${baseUrl}/security/token`;

	if (tenant.auth.tokenFormat === 'formdata') {
		const formData = new FormData();
		formData.append('grant_type', 'password');
		formData.append('username', email);
		formData.append('password', password);

		const response = await axios.post<TLogin>(url, formData);

		return response.data;
	}

	const params = new URLSearchParams();
	params.append('grant_type', 'password');
	params.append('username', email);
	params.append('password', password);

	const response = await axios.post<TLogin>(url, params, {
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});

	return response.data;
};

type TSessionData = Pick<TLogin, 'access_token' | 'sid' | 'groupsid' | 'nameidentifier' | 'userdata'>;

const SESSION_KEYS: (keyof TSessionData)[] = [
	'access_token',
	'sid',
	'groupsid',
	'nameidentifier',
	'userdata',
];

/** Chave unica em localStorage usada pelo "Manter-me conectado". */
const PERSISTED_SESSION_KEY = `${tenant.id}:persisted_session`;

interface SaveSessionOptions {
	/**
	 * Quando true ("Manter-me conectado"), a sessao tambem e gravada em localStorage
	 * e restaurada automaticamente na proxima visita (ver restorePersistedSession).
	 */
	persist?: boolean;
}

export const saveSession = (loginData: TSessionData, options: SaveSessionOptions = {}) => {
	SESSION_KEYS.forEach((key) => sessionStorage.setItem(key, loginData[key]));

	try {
		if (options.persist) {
			const payload: TSessionData = {
				access_token: loginData.access_token,
				sid: loginData.sid,
				groupsid: loginData.groupsid,
				nameidentifier: loginData.nameidentifier,
				userdata: loginData.userdata,
			};
			localStorage.setItem(PERSISTED_SESSION_KEY, JSON.stringify(payload));
		} else {
			localStorage.removeItem(PERSISTED_SESSION_KEY);
		}
	} catch {
		// localStorage indisponivel (modo privado, cota, etc.) — segue apenas com sessionStorage
	}
};

/**
 * Restaura em sessionStorage uma sessao persistida por "Manter-me conectado".
 * Retorna true se havia sessao para restaurar.
 */
export const restorePersistedSession = (): boolean => {
	try {
		const raw = localStorage.getItem(PERSISTED_SESSION_KEY);
		if (!raw) return false;

		const data = JSON.parse(raw) as Partial<TSessionData>;
		if (!data.access_token || !data.sid) {
			localStorage.removeItem(PERSISTED_SESSION_KEY);
			return false;
		}

		SESSION_KEYS.forEach((key) => sessionStorage.setItem(key, data[key] ?? ''));
		return true;
	} catch {
		return false;
	}
};

export const clearSession = () => {
	SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));

	try {
		localStorage.removeItem(PERSISTED_SESSION_KEY);
	} catch {
		// ignore
	}
};

export const isAuthenticated = (): boolean => {
	const token = sessionStorage.getItem('access_token');
	const sid = sessionStorage.getItem('sid');
	return !!token && !!sid;
};
